import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  Divider
} from '@mui/material';
import axios from 'axios';
import { useToast } from '../../contexts/ToastContext';

/**
 * Planning replies to CA's clarification request. Intimation status goes back to PENDING.
 */
const PlanningReplyToClarificationDialog = ({ open, onClose, intimation, onSuccess }) => {
  const [reply, setReply] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const { showToast } = useToast();

  useEffect(() => {
    if (intimation) {
      setReply('');
      setError('');
    }
  }, [intimation]);

  const handleSubmit = async () => {
    if (!intimation) return;
    try {
      setLoading(true);
      setError('');
      const replyText = (reply || '').trim();
      if (!replyText) {
        setError('Reply is required');
        return;
      }
      await axios.post(`/api/intimations/${intimation._id}/planning-reply`, { reply: replyText });
      showToast('Reply sent. Intimation is back to PENDING for CA to acknowledge.', 'success');
      onSuccess();
      onClose();
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to send reply';
      setError(msg);
      showToast(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  if (!intimation) return null;

  const requestedBy = intimation.clarificationRequestedBy
    ? `${intimation.clarificationRequestedBy.firstName || ''} ${intimation.clarificationRequestedBy.lastName || ''}`.trim() || 'CA'
    : 'CA Manager / CA Technical Officer';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Reply to Clarification Request</DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Clarification requested <strong>by</strong> {requestedBy} <strong>from</strong> Planning.
          </Typography>
          <Typography variant="body2">
            Planning can reply below to send this intimation back to PENDING so CA can acknowledge or request further clarification.
          </Typography>
        </Alert>

        {intimation.clarificationRemarks && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>CA&apos;s question:</Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{intimation.clarificationRemarks}</Typography>
          </Box>
        )}

        {intimation.planningClarificationReply && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>Your previous reply:</Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{intimation.planningClarificationReply}</Typography>
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <TextField
          fullWidth
          multiline
          rows={4}
          label="Your reply *"
          value={reply}
          onChange={(e) => setReply(e.target.value)}
          placeholder="Provide the clarification requested by CA..."
          required
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading || !(reply || '').trim()}>
          {loading ? 'Sending...' : 'Send reply'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PlanningReplyToClarificationDialog;
